/**
 * Money is always handled as **integer paise** (1 rupee = 100 paise). No floats, ever.
 * These helpers keep arithmetic and rounding consistent across the codebase.
 */

const PAISE_PER_RUPEE = 100;

export type Paise = number; // branded as `Paise` in @terra/contracts; plain number at the util layer

/** Convert a rupee amount (possibly fractional, e.g. from user input) to integer paise. */
export function rupeesToPaise(rupees: number): Paise {
  if (!Number.isFinite(rupees)) throw new RangeError('rupeesToPaise: non-finite input');
  return Math.round(rupees * PAISE_PER_RUPEE);
}

/**
 * Parse a rupee amount typed by a person or read from a spreadsheet cell —
 * `"499"`, `"499.5"`, `"₹1,299.00"` — into integer paise, exactly.
 *
 * Done on the digits rather than through `parseFloat`, which cannot hold most
 * decimal fractions: `Math.round(parseFloat("1.005") * 100)` is 100, not 101.
 * Returns `null` for anything that is not a plain non-negative amount with at
 * most two decimals, so a typo is reported instead of silently rounded.
 */
export function parseRupeesToPaise(input: string): Paise | null {
  const s = input
    .trim()
    .replace(/^(₹|rs\.?|inr)\s*/i, '')
    .replace(/,/g, '');
  const m = /^(\d{1,9})(?:\.(\d{1,2}))?$/.exec(s);
  if (!m) return null;
  return Number(m[1]) * PAISE_PER_RUPEE + Number((m[2] ?? '').padEnd(2, '0'));
}

/** Convert integer paise to a rupee number (for display/serialisation only — never for math). */
export function paiseToRupees(paise: Paise): number {
  return paise / PAISE_PER_RUPEE;
}

/** Sum a list of paise amounts. */
export function sumPaise(amounts: readonly Paise[]): Paise {
  return amounts.reduce((a, b) => a + b, 0);
}

/** Multiply a paise amount by an integer quantity. */
export function multiplyPaise(unit: Paise, qty: number): Paise {
  if (!Number.isInteger(qty) || qty < 0)
    throw new RangeError('multiplyPaise: qty must be a non-negative integer');
  return unit * qty;
}

/**
 * Apply a percentage given in **basis points** (10000 bps = 100%) and round to whole paise.
 * Used for commission, discount, and tax math.
 */
export function applyBps(amount: Paise, bps: number): Paise {
  if (!Number.isInteger(bps) || bps < 0)
    throw new RangeError('applyBps: bps must be a non-negative integer');
  return Math.round((amount * bps) / 10_000);
}

/** Clamp to >= 0 (defensive for refund/credit math). */
export function nonNegative(amount: Paise): Paise {
  return amount < 0 ? 0 : amount;
}

const INR_FORMATTER = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
});

/** Format integer paise as an en-IN INR string, e.g. 1234567 -> "₹12,345.67". */
export function formatINR(paise: Paise): string {
  return INR_FORMATTER.format(paiseToRupees(paise));
}

/** Format without the currency symbol, e.g. 1234567 -> "12,345.67". */
export function formatAmount(paise: Paise): string {
  return new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2 }).format(paiseToRupees(paise));
}

/**
 * Display helpers, for screens rather than ledgers.
 *
 * `formatINR` above is the exact one — full precision, for anywhere an amount
 * has to reconcile. These three are for reading: a product tile does not need
 * two decimal places, and a lifetime-earnings figure is easier to read as
 * "₹1.84L" than as "₹1,84,250.00".
 *
 * All three take paise, like everything else here. None of them is safe to
 * parse back into a number.
 */

/** Full precision with the rupee symbol — the default for a price. */
export function formatPrice(paise: Paise): string {
  return formatINR(paise);
}

/** Drops the paise. For prices that are whole rupees anyway, and for tiles. */
export function formatPriceNoDecimals(paise: Paise): string {
  return `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(
    paiseToRupees(paise),
  )}`;
}

/** Whole rupees with Indian digit grouping: 1234567 → "12,34,567". */
function groupIndian(n: number): string {
  const digits = String(Math.round(Math.abs(n)));
  if (digits.length <= 3) return digits;
  const last3 = digits.slice(-3);
  const rest = digits.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  return `${rest},${last3}`;
}

/** Up to two decimals, without trailing zeros: 1.50 → "1.5", 2.00 → "2". */
const trim2 = (n: number) => String(Number(n.toFixed(2)));

/**
 * An amount the way people in India read it on a dashboard:
 *   under a lakh  → the full figure, Indian grouping: ₹2,435 · ₹99,999
 *   lakhs         → ₹1.25 L · ₹12.5 L
 *   crores        → ₹2.5 Cr · ₹120 Cr
 * Never "K" or "M": ₹2.4K reads as a Western figure, and below a lakh the
 * exact rupees are short enough to show in full.
 *
 * Deliberately not `Intl` 'compact' notation, which renders differently by
 * runtime (React Native's ICU build is not the browser's); the arithmetic here
 * reads the same on every platform.
 */
export function formatPriceShort(paise: Paise): string {
  const rupees = paiseToRupees(paise);
  const abs = Math.abs(rupees);
  const sign = rupees < 0 ? '-' : '';
  // Rounded first, so ₹99,99,999 is "₹1 Cr", not "₹100 L".
  if (Number((abs / 1e5).toFixed(2)) >= 100) return `${sign}₹${trim2(abs / 1e7)} Cr`;
  if (abs >= 1e5) return `${sign}₹${trim2(abs / 1e5)} L`;
  return `${sign}₹${groupIndian(abs)}`;
}
